import { Link, useLocation } from 'react-router-dom'
import { IconChevronDown } from '@tabler/icons-react'
import { Button, buttonVariants } from './custom/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip'
import { cn } from '@/lib/utils'
import useCheckActiveNav from '@/hooks/use-check-active-nav'
import { SideLink } from '@/data/sidelinks'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'

interface NavProps extends React.HTMLAttributes<HTMLDivElement> {
  isCollapsed: boolean
  links: SideLink[]
  closeNav: () => void
}

export default function Nav({
  links,
  isCollapsed,
  className,
  closeNav,
}: NavProps) {
  
  const renderLink = ({ sub, ...rest }: SideLink) => {
    const key = `${rest.title}-${rest.href}`
    if (isCollapsed && sub)
      return (
        <NavLinkIconDropdown
          {...rest}
          sub={sub}
          key={key}
          closeNav={closeNav}
        />
      )

    if (isCollapsed)
      return <NavLinkIcon {...rest} key={key} closeNav={closeNav} />

    if (sub)
      return (
        <NavLinkDropdown {...rest} sub={sub} key={key} closeNav={closeNav} />
      )

    return <NavLink {...rest} key={key} closeNav={closeNav} />
  }
  
  return (
    <div
      data-collapsed={isCollapsed}
      className={cn(
        'scrollbar-hide group flex flex-col gap-3 transition-all duration-300',
        className
      )}
    >
      <TooltipProvider delayDuration={0}>
        <nav className="flex flex-col gap-2">
          {links.map(renderLink)}
        </nav>
      </TooltipProvider>
    </div>
  )
}

interface NavLinkProps extends SideLink {
  subLink?: boolean
  closeNav: () => void
}

function NavLink({
  title,
  icon,
  label,
  href,
  closeNav,
  subLink = false,
}: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()
  const location = useLocation()
  
  // Normalize href
  const normalizedHref = href.startsWith('/') ? href : `/${href}`
  
  // For sublevel links, use more specific matching
  const isActive = subLink 
    ? location.pathname === normalizedHref || checkActiveNav(normalizedHref)
    : checkActiveNav(normalizedHref)
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.1 }}
    >
      <Link
        to={href}
        onClick={closeNav}
        className={cn(
          buttonVariants({
            variant: isActive ? 'secondary' : 'ghost',
            size: 'sm',
          }),
          'flex h-10 w-full items-center justify-start gap-3 rounded-lg px-3 text-sm transition-all duration-200',
          'hover:bg-accent hover:text-accent-foreground',
          // Different font weights for parent vs submenu
          subLink ? 'font-normal' : 'font-medium',
          subLink && 'ml-6 h-8 border-l-2 border-muted-foreground/20 pl-4 rounded-l-none',
          isActive && 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className={cn(
          "flex h-5 w-5 items-center justify-center transition-colors",
          isActive && "text-primary"
        )}>
          {icon}
        </div>
        <span className="flex-1 truncate text-left">{title}</span>
        {label && (
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="rounded-md bg-primary px-1.5 py-0.5 text-xs font-medium text-primary-foreground"
          >
            {label}
          </motion.div>
        )}
      </Link>
    </motion.div>
  )
}

function NavLinkDropdown({ title, icon, label, sub, closeNav }: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  // Normalize href by adding leading slash if missing
  const normalizeHref = (href: string) => {
    return href.startsWith('/') ? href : `/${href}`
  }

  // Check if current route matches any sub-item (comprehensive check)
  const isParentActive = !!sub?.some((s) => {
    const normalizedSubHref = normalizeHref(s.href)
    // Check exact match first
    if (checkActiveNav(normalizedSubHref)) return true
    // Check if current path starts with sub-item path (for nested routes)
    if (location.pathname.startsWith(normalizedSubHref)) return true
    // Check if current path exactly matches sub-item href
    if (location.pathname === normalizedSubHref) return true
    return false
  })

  const hasActiveChild = isParentActive

  // Auto-open if any child is active
  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true)
    }
  }, [hasActiveChild, location.pathname])

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.1 }}
        >
          <Button
            variant={isParentActive ? 'secondary' : 'ghost'}
            className={cn(
              'flex h-10 w-full items-center justify-start gap-3 rounded-lg px-3 text-sm font-medium transition-all duration-200',
              'hover:bg-accent hover:text-accent-foreground',
              isParentActive && 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
            )}
          >
            <div className={cn(
              "flex h-5 w-5 items-center justify-center transition-colors",
              isParentActive && "text-primary"
            )}>
              {icon}
            </div>
            <span className="flex-1 truncate text-left">{title}</span>
            {label && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="rounded-md bg-primary px-1.5 py-0.5 text-xs font-medium text-primary-foreground"
              >
                {label}
              </motion.div>
            )}
            <motion.div
              animate={{ 
                rotate: isOpen ? 180 : 0,
                transition: { duration: 0.2 }
              }}
              className="ml-auto"
            >
              <IconChevronDown className="h-4 w-4" />
            </motion.div>
          </Button>
        </motion.div>
      </CollapsibleTrigger>
      
      <AnimatePresence>
        {isOpen && (
          <CollapsibleContent asChild forceMount>
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="mt-2 space-y-2">
                {sub!.map((sublink) => (
                  <NavLink 
                    key={sublink.title} 
                    {...sublink} 
                    subLink 
                    closeNav={closeNav} 
                  />
                ))}
              </div>
            </motion.div>
          </CollapsibleContent>
        )}
      </AnimatePresence>
    </Collapsible>
  )
}

function NavLinkIcon({ title, icon, label, href, closeNav }: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()
  const isActive = checkActiveNav(href)
  
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.1 }}
        >
          <Link
            to={href}
            onClick={closeNav}
            className={cn(
              buttonVariants({
                variant: isActive ? 'secondary' : 'ghost',
                size: 'icon',
              }),
              'h-10 w-10 rounded-lg transition-all duration-200',
              isActive && 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
            )}
          >
            <div className={cn(
              "flex h-5 w-5 items-center justify-center transition-colors",
              isActive && "text-primary"
            )}>
              {icon}
            </div>
            <span className="sr-only">{title}</span>
          </Link>
        </motion.div>
      </TooltipTrigger>
      <TooltipContent 
        side="right" 
        className="flex items-center gap-4 rounded-lg border bg-popover px-3 py-2 text-sm shadow-md"
        sideOffset={10}
      >
        <span className="font-medium">{title}</span>
        {label && (
          <span className="rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
            {label}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function NavLinkIconDropdown({ title, icon, label, sub, closeNav }: NavLinkProps) {
  const { checkActiveNav } = useCheckActiveNav()
  const location = useLocation()

  // Normalize href by adding leading slash if missing
  const normalizeHref = (href: string) => {
    return href.startsWith('/') ? href : `/${href}`
  }

  // Check if any child is active (comprehensive check)
  const hasActiveChild = !!sub?.some((s) => {
    const normalizedSubHref = normalizeHref(s.href)
    // Check exact match first
    if (checkActiveNav(normalizedSubHref)) return true
    // Check if current path starts with sub-item path (for nested routes)
    if (location.pathname.startsWith(normalizedSubHref)) return true
    // Check if current path exactly matches sub-item href
    if (location.pathname === normalizedSubHref) return true
    return false
  })

  return (
    <DropdownMenu>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            >
              <Button
                variant={hasActiveChild ? 'secondary' : 'ghost'}
                size="icon"
                className={cn(
                  'h-10 w-10 rounded-lg transition-all duration-200',
                  hasActiveChild && 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
                )}
              >
                <div className={cn(
                  "flex h-5 w-5 items-center justify-center transition-colors",
                  hasActiveChild && "text-primary"
                )}>
                  {icon}
                </div>
              </Button>
            </motion.div>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent 
          side="right" 
          className="flex items-center gap-4 rounded-lg border bg-popover px-3 py-2 text-sm shadow-md"
          sideOffset={10}
        >
          <span className="font-medium">{title}</span>
          {label && (
            <span className="rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
              {label}
            </span>
          )}
          <IconChevronDown size={16} className="-rotate-90 text-muted-foreground" />
        </TooltipContent>
      </Tooltip>
      
      <DropdownMenuContent 
        side="right" 
        align="start" 
        sideOffset={10}
        className="w-48 rounded-lg border bg-popover shadow-lg"
      >
        <DropdownMenuLabel className="px-3 py-2 text-sm font-semibold">
          {title}
          {label && (
            <span className="ml-2 rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
              {label}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sub!.map(({ title, icon, label, href }) => {
          const normalizedHref = href.startsWith('/') ? href : `/${href}`
          const isActive = checkActiveNav(normalizedHref) || 
            location.pathname.startsWith(normalizedHref) ||
            location.pathname === normalizedHref
          return (
            <DropdownMenuItem key={`${title}-${href}`} asChild>
              <Link
                to={href}
                onClick={closeNav}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive && 'bg-primary/10 text-primary'
                )}
              >
                <div className={cn(
                  "flex h-4 w-4 items-center justify-center",
                  isActive && "text-primary"
                )}>
                  {icon}
                </div>
                <span className="flex-1 truncate text-left">{title}</span>
                {label && (
                  <span className="rounded bg-primary px-1.5 py-0.5 text-xs text-primary-foreground">
                    {label}
                  </span>
                )}
              </Link>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}